<template>
  <AppAccordion
    v-model:open="open"
    :data-auto-testid="$attrs['data-auto-testid'] || null"
    :data-testid="$attrs['data-testid'] || null"
    :prepend-icon="icon"
    class="hpc-Card"
  >
    <template #title>
      <slot name="accordion:title" />
    </template>
    <template #header-right>
      <slot name="header-right">
        <HomePageElementsProgress
          v-if="hasElementsProgress"
          ref="homePageElementsProgress"
          :hide="isNoResults"
          :payload="getElementsProgressPayload"
        />
      </slot>
    </template>
    <template #content>
      <div class="hpc-Card_AccordionContent">
        <DefineTemplate>
          <AppRadioGroup
            v-if="canShowAsProgressView"
            v-model="viewMode"
            :data-auto-testid="HOMEPAGE_CARD_VIEW_SWITCH_TEST_ID"
            :data-testid="HOMEPAGE_CARD_VIEW_SWITCH_TEST_ID"
            :options="VIEW_OPTIONS"
            name="view-switch"
            style="--option-padding: 0 8px 0 0"
          >
            <template #item-label="{ item }">
              <AppRadioGroupNextOptionWithIcon :option="item" />
            </template>
          </AppRadioGroup>
        </DefineTemplate>

        <div v-if="canShowAsProgressView && isListModeEnabled && !isNoResults" class="hpc-Card_Nav">
          <ReuseTemplate />
        </div>

        <template v-if="isListModeEnabled">
          <slot v-if="!isNoResults" :with-top-border="canShowAsProgressView" name="header" />
          <PersonalStatisticsOkrElementsList
            ref="okrsList"
            :list-type="listType"
            :search-type-id="searchTypeId"
            :selected-dates="selectedDates"
            :selected-intervals="selectedIntervals"
            :selected-user-name="selectedUserName"
            :selected-user-or-group="selectedUserOrGroup"
            :show-last-checkin="showLastCheckin"
            :user-selected="userSelected"
            hide-header
            no-divider
            @edit-element="$emit('edit-element', $event)"
            @create-jira-issue="$emit('create-jira-issue', $event)"
            @link-jira-issue="linkJiraIssue($event)"
            @link-work-item="linkWorkItem($event)"
            @create-work-item="$emit('create-work-item', $event)"
            @create-key-result="$emit('create-key-result', $event)"
            @update-okr-elements="$emit('update-okr-elements', $event)"
            @okr-element-deleted="$emit('update-okr-elements')"
            @open-checkin-modal="$emit('open-checkin-modal', $event)"
            @empty-change="onEmptyChange"
          >
            <template #loader="{ loading }">
              <slot v-if="loading" name="loader" />
            </template>
          </PersonalStatisticsOkrElementsList>
        </template>

        <PersonalStatisticsProgressView
          v-else
          ref="progressView"
          :selected-dates="selectedDates"
          :selected-intervals="selectedIntervals"
          :selected-user-or-group="selectedUserOrGroup"
          :user-selected="userSelected"
          @edit-element="$emit('edit-element', $event)"
          @open-checkin-modal="$emit('open-checkin-modal', $event)"
          @update-okr-elements="$emit('update-okr-elements')"
          @empty-change="onEmptyChange"
        >
          <template #before-actions>
            <ReuseTemplate />
          </template>
        </PersonalStatisticsProgressView>
      </div>
    </template>
  </AppAccordion>
</template>

<script setup>
import { createReusableTemplate } from '@vueuse/core'
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'

import { SEARCH_TYPES } from '@/api/okr-elements'
import { USER_LIST_TYPES } from '@/utils/homepage'
import { OKR_FORM_VIEWS, OKR_VIEW_PAGES } from '@/utils/objectives'
import { stringOrNullProp } from '@/utils/prop-validators'
import {
  DEFAULT_USERS_SETTINGS,
  HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'

import HomePageElementsProgress from '@/components/home-page/HomePageElementsProgress'
import { HOMEPAGE_CARD_VIEW_SWITCH_TEST_ID } from '@/components/home-page/jest-helpers'
import PersonalStatisticsOkrElementsList from '@/components/personal-statistics/PersonalStatisticsOkrElementsList'
import PersonalStatisticsProgressView from '@/components/personal-statistics/PersonalStatisticsProgressView'
import AppAccordion from '@/components/ui/AppAccordion/AppAccordion'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import AppRadioGroupNextOptionWithIcon from '@/components/ui/AppRadioGroup/AppRadioGroupNextOptionWithIcon'

defineOptions({
  name: 'HomePageCard',
  inheritAttrs: false
})

const props = defineProps({
  icon: {
    type: String,
    default: null
  },

  listType: {
    type: String,
    validator: v => Object.values(USER_LIST_TYPES).includes(v),
    default: null
  },

  searchTypeId: {
    type: Number,
    default: null
  },

  selectedDates: {
    type: Object,
    required: true
  },

  selectedIntervals: {
    type: Array,
    required: true
  },

  selectedUserName: {
    default: '',
    validator: v => stringOrNullProp(v)
  },

  selectedUserOrGroup: {
    type: [String, Number],
    required: true
  },

  showLastCheckin: {
    type: Boolean
  },

  userSelected: {
    type: Boolean
  },

  hasElementsProgress: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits({
  'edit-element': null,
  'link-jira-issue': null,
  'create-jira-issue': null,
  'link-work-item': null,
  'create-work-item': null,
  'create-key-result': null,
  'update-okr-elements': null,
  'open-checkin-modal': null
})

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const open = defineModel('open', {
  type: Boolean,
  default: true
})

const okrsList = useTemplateRef('okrsList')
const progressView = useTemplateRef('progressView')
const isNoResults = ref(false)

const onEmptyChange = empty => {
  isNoResults.value = empty
}

const homePageElementsProgressRef = useTemplateRef('homePageElementsProgress')

const getElementsProgressPayload = computed(() => {
  return {
    listType: props.listType,
    searchTypeId: props.searchTypeId,
    selectedUserOrGroup: props.selectedUserOrGroup,
    userSelected: props.userSelected,
    selectedDates: props.selectedDates,
    selectedIntervals: props.selectedIntervals
  }
})

const getElementsProgress = async () => {
  await homePageElementsProgressRef.value?.refreshData()
}

const refreshOkrLists = async () => {
  await nextTick()
  await okrsList.value?.refreshData()
  getElementsProgress()
}

const updateOkrElements = async () => {
  await nextTick()
  await Promise.all([okrsList.value?.updateElements(), progressView.value?.updateElements()])
  getElementsProgress()
}

const canShowAsProgressView = computed(() => {
  return props.searchTypeId === SEARCH_TYPES.METRICS_WITHOUT_KPI
})

onMounted(refreshOkrLists)

watch(open, isOpen => {
  if (isOpen) {
    refreshOkrLists()
  }
})

defineExpose({
  refreshData: refreshOkrLists,
  updateOkrElements
})

const VIEW_OPTIONS = [
  {
    label: 'view_select.list',
    value: OKR_VIEW_PAGES.ALIGNMENT,
    icon: 'roadmap-switch-list'
  },
  {
    label: 'view_select.progress',
    value: OKR_VIEW_PAGES.PROGRESS,
    icon: 'view-select-progress'
  }
]

const store = useStore()

const metricsCardConfig = computed(
  () => store.getters['system/homepageMetricsWithoutKpiCardConfig']
)

const savedViewMode = metricsCardConfig.value.activeTab

const viewMode = ref(
  VIEW_OPTIONS.some(option => option.value === savedViewMode)
    ? savedViewMode
    : DEFAULT_USERS_SETTINGS[HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG].activeTab
)

watch(viewMode, activeTab => {
  store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[HOMEPAGE_METRICS_WITHOUT_KPI_CARD_CONFIG]]: {
      ...metricsCardConfig.value,
      activeTab
    }
  })
})

const isListModeEnabled = computed(() => {
  if (canShowAsProgressView.value) {
    return viewMode.value === OKR_VIEW_PAGES.ALIGNMENT
  }

  return true
})

watch(isListModeEnabled, isEnabled => {
  if (isEnabled) {
    refreshOkrLists()
  }
})

const linkJiraIssue = data => {
  emit('link-jira-issue', { view: OKR_FORM_VIEWS.LINK_JIRA_ISSUE, data })
}

const linkWorkItem = data => {
  emit('link-work-item', { view: OKR_FORM_VIEWS.LINK_WORK_ITEM, data })
}
</script>

<style lang="scss" scoped>
$space: 24px;

.hpc-Card {
  --ob-aa-prepend-icon-color: #{$grey-1-next};
  --ob-aa-chevron-color: #{$grey-1-next};
}

.hpc-Card_AccordionContent {
  --item-padding-left: #{$space};
  --item-padding-right: #{$space};
  --ob-psoe-margin-horizontal: 0px;
  --ob-alm-margin: 0 0 calc(#{$space} / 2) #{$space};
  --right-items-gap: #{$space};

  --ob-oee-border: none;
  --ob-oee-padding: #{$space} #{$space * 1.5};
}

.hpc-Card_Nav:not(:empty) {
  padding: 9px $space;
}
</style>
